import { RefObject, useEffect } from 'react';

import { clientToCssPixels } from '../../utils/displaySize';

import {
  STRIP_GLIDE_NONE,
  applyStripOffset,
  clampStripOffset,
  readStripOffset,
  readTabStripMetrics,
} from './tabStripOffset';

/** Travel, in px, before a press counts as a drag of the strip rather than a tap on a tab. */
const DRAG_THRESHOLD_PX = 4;

/** WheelEvent.DOM_DELTA_LINE: the delta counts text lines, not pixels. */
const DOM_DELTA_LINE = 1;

/** What a line of wheel travel is worth in px, for the hosts that report deltas that way. */
const WHEEL_LINE_HEIGHT_PX = 16;

/**
 * A trackpad reports sideways travel on deltaX; a wheel only ever turns deltaY, and over a strip
 * that moves in one axis the browser's own convention is to spend it there.
 */
const resolveWheelTravel = (event: WheelEvent) => {
  const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
  return event.deltaMode === DOM_DELTA_LINE ? delta * WHEEL_LINE_HEIGHT_PX : delta;
};

/**
 * Makes the tab strip answer to the wheel, a trackpad swipe and a drag, so the tabs that overflow
 * are reachable without going through the overflow menu.
 *
 * rc-tabs ships its own versions of these, but they all move the strip by writing its inline
 * transform, and this component's offset outranks that transform deliberately. Nothing is left to
 * take the gesture, so it is taken here and spent on the offset instead.
 */
export const useScrollableTabStrip = (rootRef: RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    /* Only the strip's own row takes the gesture. A nested tab bar sits in the content pane
       rather than in this nav, so it is excluded here and claims its own wheel events itself. */
    const isOnTabBar = (target: EventTarget | null) =>
      target instanceof Node && !!root.querySelector('.ant-tabs-nav')?.contains(target);

    const scrollStripBy = (distance: number) => {
      const metrics = readTabStripMetrics(root);
      if (!metrics || metrics.furthestOffset === 0) return false;

      const current = readStripOffset(metrics.tabList);
      const from = Number.isFinite(current) ? current : 0;
      const next = clampStripOffset(from + distance, metrics.furthestOffset);
      if (next === from) return false;

      applyStripOffset(metrics.tabList, next, STRIP_GLIDE_NONE);
      return true;
    };

    const onWheel = (event: WheelEvent) => {
      if (!isOnTabBar(event.target)) return;
      event.stopPropagation();
      if (scrollStripBy(-resolveWheelTravel(event))) event.preventDefault();
    };

    let dragPointerId: number | null = null;
    let dragOriginX = 0;
    let dragStartOffset = 0;
    let hasDragged = false;

    const onPointerDown = (event: PointerEvent) => {
      hasDragged = false;
      if (!event.isPrimary || !isOnTabBar(event.target)) return;

      const metrics = readTabStripMetrics(root);
      if (!metrics || metrics.furthestOffset === 0) return;

      const current = readStripOffset(metrics.tabList);
      dragPointerId = event.pointerId;
      dragOriginX = event.clientX;
      dragStartOffset = Number.isFinite(current) ? current : 0;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== dragPointerId) return;

      const travel = clientToCssPixels(event.clientX - dragOriginX);
      if (!hasDragged && Math.abs(travel) < DRAG_THRESHOLD_PX) return;

      const metrics = readTabStripMetrics(root);
      if (!metrics) return;

      if (!hasDragged) {
        hasDragged = true;
        // keeps the drag alive once the pointer leaves the strip's own row
        root.setPointerCapture(event.pointerId);
      }
      const offset = clampStripOffset(dragStartOffset + travel, metrics.furthestOffset);
      applyStripOffset(metrics.tabList, offset, STRIP_GLIDE_NONE);
    };

    const endDrag = (event: PointerEvent) => {
      if (event.pointerId !== dragPointerId) return;
      if (root.hasPointerCapture(event.pointerId)) root.releasePointerCapture(event.pointerId);
      dragPointerId = null;
    };

    /* A drag that comes to rest over a tab must not also select it. The click is swallowed on the
       way down, before it reaches the handler rc-tabs puts on the tab itself. */
    const onClickCapture = (event: MouseEvent) => {
      if (!hasDragged || !isOnTabBar(event.target)) return;
      hasDragged = false;
      event.preventDefault();
      event.stopPropagation();
    };

    root.addEventListener('wheel', onWheel, { capture: true, passive: false });
    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('pointermove', onPointerMove);
    root.addEventListener('pointerup', endDrag);
    root.addEventListener('pointercancel', endDrag);
    root.addEventListener('click', onClickCapture, true);

    return () => {
      root.removeEventListener('wheel', onWheel, { capture: true });
      root.removeEventListener('pointerdown', onPointerDown);
      root.removeEventListener('pointermove', onPointerMove);
      root.removeEventListener('pointerup', endDrag);
      root.removeEventListener('pointercancel', endDrag);
      root.removeEventListener('click', onClickCapture, true);
    };
  }, [rootRef]);
};
